(() => {
  'use strict';

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const fill = (template, values) => String(template).replace(/\[([^\]]+)\]/g, (token, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : token
  ));

  function initialise(root) {
    if (root.dataset.zkInitialised) return;
    root.dataset.zkInitialised = '1';
    const find = (selector) => root.querySelector(selector);
    const select = find('[data-zk-plaats]');
    const error = find('[data-zk-fout]');
    const loading = find('[data-zk-laden]');
    const result = find('[data-zk-resultaat]');
    const headline = find('[data-zk-kerncijfer]');
    const csvButton = find('[data-zk-csv]');
    const pngButton = find('[data-zk-png]');
    let texts;

    function fail() {
      delete root.dataset.klaar;
      if (texts && texts.fout) error.textContent = texts.fout;
      error.hidden = false;
      loading.hidden = true;
      result.style.visibility = 'hidden';
      headline.replaceChildren();
      headline.hidden = true;
      csvButton.disabled = true;
      pngButton.disabled = true;
    }

    try {
      texts = JSON.parse(find('[data-zk-teksten]').textContent);
      const data = JSON.parse(find('[data-zk-data]').textContent);
      if (!texts || !data || !Array.isArray(data.steden) || !data.steden.length) throw new Error();
      const locale = root.dataset.locale || 'en';
      const language = locale.toLowerCase().split('-')[0];
      const languages = [...new Set([locale, language, ...(language === 'nb' ? ['no'] : language === 'no' ? ['nb'] : [])])];
      const number = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      const dayCount = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
      const collator = new Intl.Collator(locale);
      const shortMonth = new Intl.DateTimeFormat(locale, { month: 'short' });
      const longMonth = new Intl.DateTimeFormat(locale, { month: 'long' });
      const months = Array.from({ length: 12 }, (_, index) => {
        const date = new Date(2001, index, 1);
        return { number: index + 1, short: shortMonth.format(date), long: longMonth.format(date) };
      });
      const cityName = (city) => languages.map((key) => city.namen[key]).find(Boolean) || city.namen.standaard;
      const percentage = (value) => `${number.format(value)}%`;
      const dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data.bijgewerkt);
      if (!dateParts) throw new Error();
      const date = new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]));
      const source = fill(texts.bron, {
        datum: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date)
      });
      const cities = [...data.steden].sort((a, b) => collator.compare(cityName(a), cityName(b)));
      select.replaceChildren(...cities.map((city) => {
        const option = element('option', '', cityName(city));
        option.value = city.id;
        return option;
      }));
      const defaultCity = languages.map((key) => cities.find((city) => Array.isArray(city.standaard_voor) && city.standaard_voor.includes(key))).find(Boolean);
      select.value = (defaultCity || cities[0]).id;
      select.disabled = false;
      let current;

      const statusNote = (row) => {
        if (row.status !== 'te_weinig' && row.status !== 'lopend') return '';
        return fill(row.status === 'te_weinig' ? texts.te_weinig : texts.niet_compleet, {
          gemeten: dayCount.format(row.gemeten),
          totaal: dayCount.format(row.totaal),
          verstreken: dayCount.format(row.verstreken)
        });
      };

      function monthLabel(month) {
        const label = element('span');
        label.append(
          element('span', 'nft-zk__maand-kort', month.short),
          element('span', 'nft-zk__maand-lang', month.long)
        );
        label.setAttribute('aria-label', month.long);
        return label;
      }

      function readCity(city) {
        if (!city || !city.reeksen || !Array.isArray(city.reeksen.klimatologie)) throw new Error();
        const years = Object.keys(city.reeksen).filter((key) => key !== 'klimatologie').sort((a, b) => Number(a) - Number(b));
        const series = ['klimatologie', ...years].map((key) => {
          const rows = city.reeksen[key];
          if (!Array.isArray(rows) || rows.length !== 12) throw new Error();
          const ordered = months.map((month) => rows.find((row) => row.maand === month.number));
          ordered.forEach((row) => {
            if (!row || !['ok', 'te_weinig', 'lopend', 'toekomst'].includes(row.status)) throw new Error();
            if ((row.status === 'ok' || row.status === 'lopend') && (!Number.isFinite(row.procent) || !Number.isFinite(row.waarde))) throw new Error();
          });
          return {
            key,
            label: key === 'klimatologie' ? texts.reeks_klimatologie : key,
            kind: key === 'klimatologie' ? 'klimatologie' : key === years[years.length - 1] ? 'lopend' : 'vorig',
            rows: ordered
          };
        });
        return { city, name: cityName(city), series, years };
      }

      function drawHeadline(model) {
        headline.replaceChildren();
        const valid = model.series[0].rows.filter((row) => row.status === 'ok').sort((a, b) => a.waarde - b.waarde);
        headline.hidden = valid.length < 2;
        if (headline.hidden) return;
        const weakest = valid[0];
        const strongest = valid[valid.length - 1];
        const values = {
          plaats: model.name,
          zwakste_maand: months[weakest.maand - 1].long,
          sterkste_maand: months[strongest.maand - 1].long
        };
        const template = String(texts.kerncijfer).replace(/\[procent\]\s*[%\u066a]/g, '[procent]');
        template.split(/(\[procent\])/).forEach((part) => {
          headline.append(part === '[procent]'
            ? element('strong', '', percentage(weakest.procent))
            : document.createTextNode(fill(part, values)));
        });
      }

      function drawGraph(model) {
        const legend = find('[data-zk-legenda]');
        legend.replaceChildren(...model.series.map((series) => {
          const item = element('li');
          const swatch = element('span', `nft-zk__staal nft-zk__balk nft-zk__balk--${series.kind}`);
          swatch.setAttribute('aria-hidden', 'true');
          item.append(swatch, element('span', '', series.label));
          return item;
        }));
        const graph = find('[data-zk-grafiek]');
        graph.setAttribute('aria-label', `${texts.titel}: ${model.name}. ${headline.textContent}`);
        graph.replaceChildren(...months.map((month, index) => {
          const row = element('div', 'nft-zk__maand');
          row.dataset.zkMaand = String(month.number);
          const label = element('div', 'nft-zk__maandnaam');
          label.append(monthLabel(month));
          const bars = element('div', 'nft-zk__reeksen');
          model.series.forEach((series) => {
            const datum = series.rows[index];
            const entry = element('div', 'nft-zk__reeks');
            entry.dataset.zkReeks = series.key;
            entry.dataset.zkStatus = datum.status;
            if (datum.status === 'ok' || datum.status === 'lopend') {
              const measurement = element('div', 'nft-zk__meting');
              const track = element('div', 'nft-zk__spoor');
              const bar = element('span', `nft-zk__balk nft-zk__balk--${series.kind}`);
              bar.style.inlineSize = `${Math.max(0, Math.min(100, datum.procent))}%`;
              if (datum.procent <= 0) bar.hidden = true;
              track.append(bar);
              measurement.append(track, element('span', 'nft-zk__waarde', percentage(datum.procent)));
              entry.append(measurement);
            }
            const note = statusNote(datum);
            if (note) entry.append(element('span', 'nft-zk__status', note));
            bars.append(entry);
          });
          row.append(label, bars);
          return row;
        }));
      }

      function drawTable(model) {
        const table = find('[data-zk-tabel]');
        const head = element('thead');
        const headings = element('tr');
        [texts.kolom_maand, ...model.series.map((series) => series.label)].forEach((label) => {
          const cell = element('th', '', label);
          cell.scope = 'col';
          headings.append(cell);
        });
        head.append(headings);
        const body = element('tbody');
        months.forEach((month, index) => {
          const row = element('tr');
          const label = element('th');
          label.scope = 'row';
          label.append(monthLabel(month));
          row.append(label);
          model.series.forEach((series) => {
            const datum = series.rows[index];
            const cell = element('td');
            if (datum.status === 'ok' || datum.status === 'lopend') cell.append(element('span', 'nft-zk__waarde', percentage(datum.procent)));
            const note = statusNote(datum);
            if (note) cell.append(element('span', 'nft-zk__status', note));
            row.append(cell);
          });
          body.append(row);
        });
        table.setAttribute('aria-label', `${texts.titel}: ${model.name}`);
        table.replaceChildren(head, body);
      }

      function render() {
        delete root.dataset.klaar;
        try {
          current = readCity(cities.find((city) => city.id === select.value));
          drawHeadline(current);
          drawGraph(current);
          drawTable(current);
          find('[data-zk-bron]').textContent = source;
          error.hidden = true;
          loading.hidden = true;
          result.style.visibility = '';
          csvButton.disabled = false;
          pngButton.disabled = false;
          root.dataset.klaar = '1';
        } catch (_) {
          current = null;
          fail();
        }
      }

      function download(blob, filename) {
        if (!blob) throw new Error();
        const url = URL.createObjectURL(blob);
        const link = element('a');
        link.href = url;
        link.download = filename;
        link.hidden = true;
        root.append(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }

      function exportCSV() {
        if (!current) return;
        try {
          const csvCell = (value) => {
            const text = value === null || value === undefined ? '' : String(value);
            return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
          };
          const lines = [csvCell(source), 'plaats;reeks;maand;dosis_kj_m2;procent_van_sterkste_maand;status;gemeten_dagen;totaal_dagen'];
          current.series.forEach((series) => series.rows.forEach((row) => {
            lines.push([current.name, series.key, row.maand, row.waarde, row.procent, row.status, row.gemeten, row.totaal].map(csvCell).join(';'));
          }));
          download(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }), `zonkalender-${current.city.id}.csv`);
        } catch (_) {
          fail();
        }
      }

      async function exportPNG() {
        if (!current) return;
        const model = current;
        pngButton.disabled = true;
        try {
          if (document.fonts) await document.fonts.ready;
          const canvas = element('canvas');
          canvas.width = 1200;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error();
          const style = getComputedStyle(root);
          const rtl = style.direction === 'rtl';
          const font = style.fontFamily;
          const baseSize = parseFloat(style.fontSize) * 1.5;
          const gap = parseFloat(style.getPropertyValue('--sp-2')) * 1.5;
          const padding = parseFloat(style.getPropertyValue('--sp-7'));
          const contentWidth = canvas.width - padding * 2;
          const monthWidth = parseFloat(style.getPropertyValue('--sp-10')) * 1.5;
          const valueWidth = parseFloat(style.getPropertyValue('--sp-8')) * 1.5;
          const barHeight = parseFloat(style.getPropertyValue('--sp-3')) * 1.5;
          const ink = style.color;
          const paper = style.backgroundColor;
          const muted = getComputedStyle(find('.nft-zk__bijschrift')).color;
          const lineColor = getComputedStyle(find('.nft-zk__maand')).borderBlockStartColor;
          const paints = model.series.map((series) => {
            const bar = find(`.nft-zk__staal.nft-zk__balk--${series.kind}`);
            return getComputedStyle(bar).backgroundColor;
          });
          const commands = [];
          const start = rtl ? canvas.width - padding : padding;
          const setFont = (size, weight) => { ctx.font = `${weight} ${size}px ${font}`; };

          function textBlock(text, x, y, maxWidth, size = baseSize, weight = 400, color = ink) {
            setFont(size, weight);
            const lines = [];
            let line = '';
            String(text).split(/\s+/).filter(Boolean).forEach((word) => {
              const candidate = line ? `${line} ${word}` : word;
              if (line && ctx.measureText(candidate).width > maxWidth) {
                lines.push(line);
                line = word;
              } else {
                line = candidate;
              }
            });
            if (line) lines.push(line);
            const lineHeight = size * 1.5;
            commands.push(() => {
              setFont(size, weight);
              ctx.fillStyle = color;
              ctx.textAlign = rtl ? 'right' : 'left';
              lines.forEach((value, index) => ctx.fillText(value, x, y + index * lineHeight));
            });
            return lines.length * lineHeight;
          }

          function bar(x, y, width, paint) {
            if (width <= 0) return;
            commands.push(() => {
              ctx.fillStyle = paint;
              ctx.fillRect(x, y, width, barHeight);
            });
          }

          let y = padding;
          const titleSize = parseFloat(getComputedStyle(find('h1')).fontSize) * 1.25;
          y += textBlock(texts.titel, start, y, contentWidth, titleSize, 700) + gap;
          y += textBlock(model.name, start, y, contentWidth, baseSize * 1.25, 600) + gap * 2;
          model.series.forEach((series, index) => {
            const sampleWidth = gap * 4;
            bar(rtl ? start - sampleWidth : start, y + gap / 2, sampleWidth, paints[index]);
            y += textBlock(series.label, start + (rtl ? -1 : 1) * (sampleWidth + gap), y, contentWidth - sampleWidth - gap) + gap / 2;
          });
          y += gap;
          const plotWidth = contentWidth - monthWidth - gap * 2;
          const plotX = rtl ? padding : padding + monthWidth + gap * 2;
          const trackX = rtl ? plotX + valueWidth + gap : plotX;
          const trackWidth = plotWidth - valueWidth - gap;
          const lineHeight = baseSize * 1.5;

          months.forEach((month, monthIndex) => {
            const ruleY = y;
            commands.push(() => {
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(padding, ruleY);
              ctx.lineTo(canvas.width - padding, ruleY);
              ctx.stroke();
            });
            y += gap;
            const monthEnd = y + textBlock(month.long, start, y, monthWidth, baseSize, 600);
            model.series.forEach((series, seriesIndex) => {
              const datum = series.rows[monthIndex];
              if (datum.status === 'ok' || datum.status === 'lopend') {
                const width = datum.procent > 0 ? Math.max(2, trackWidth * Math.min(100, datum.procent) / 100) : 0;
                bar(rtl ? trackX + trackWidth - width : trackX, y + (lineHeight - barHeight) / 2, width, paints[seriesIndex]);
                textBlock(percentage(datum.procent), rtl ? plotX + valueWidth : plotX + trackWidth + gap, y, valueWidth);
              }
              const note = statusNote(datum);
              if (datum.status !== 'te_weinig') y += lineHeight;
              if (note) y += textBlock(note, rtl ? plotX + plotWidth : plotX, y, plotWidth, baseSize * 0.85, 400, muted);
              if (seriesIndex < model.series.length - 1) y += gap / 2;
            });
            y = Math.max(y, monthEnd) + gap;
          });
          y += gap;
          y += textBlock(source, start, y, contentWidth, baseSize * 0.85, 400, muted);
          canvas.height = Math.ceil(y + padding);
          ctx.fillStyle = paper;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.direction = rtl ? 'rtl' : 'ltr';
          ctx.textBaseline = 'top';
          commands.forEach((draw) => draw());
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          download(blob, `zonkalender-${model.city.id}.png`);
        } catch (_) {
          fail();
        } finally {
          pngButton.disabled = !root.hasAttribute('data-klaar');
        }
      }

      select.addEventListener('change', render);
      csvButton.addEventListener('click', exportCSV);
      pngButton.addEventListener('click', exportPNG);
      render();
    } catch (_) {
      fail();
    }
  }

  document.querySelectorAll('[data-nft-zonkalender]').forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-nft-zonkalender]').forEach(initialise);
  });
})();
